import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, ShieldAlert, Power, PowerOff, UserCog, CircleAlert, BarChart3, Plug, DatabaseZap, Trash2, Eraser, MailX, Mail, ClipboardCheck } from 'lucide-react'
import { isAdminConsoleConfigured } from '../../lib/adminMsal'
import { adminApi, adminLogin, adminLogout, getAdminAccount, AdminApiError } from '../../lib/adminApi'
import { formatDate } from '../../lib/utils'
import { BillingBackfillsDialog } from './BillingBackfillsDialog'
import { encodeImpersonationPayload, type ImpersonationSession } from '../../lib/impersonation'

// Pool-scoped admin console -- Sep 7 rock milestone. Lives inside ScopeAssistant's
// existing SWA (see App.tsx: mounted at /admin, not a separate deployed
// site) and is not linked from tenant navigation -- but that is a
// discoverability choice, not a security boundary. The actual control is
// withAdminAuth.ts on the API side: every request this page makes is
// rejected server-side unless the caller's token is issued by Raven's own
// Entra tenant and carries the Platform Ops app role. A user who guesses
// this URL sees, at most, a sign-in prompt against an app registration
// they have no access to -- reaching that point proves nothing about
// whether they can actually use it.

interface AdminTenantSummary {
  slug: string
  entraTenantId: string
  entraClientId: string
  schemaName: string
  isActive: boolean
  publicSlug: string | null
  provisioned: boolean
  pendingDeletionAt: string | null
  deprovisionInitiatedBy: string | null
  deprovisionedAt: string | null
}

function NotConfigured() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md space-y-3 text-center">
        <ShieldAlert className="mx-auto h-10 w-10 text-muted-foreground" />
        <h1 className="text-xl font-semibold text-foreground">Admin console not configured</h1>
        <p className="text-sm text-muted-foreground">
          VITE_ADMIN_ENTRA_CLIENT_ID was not set at build time -- the admin app
          registration has not been wired up for this deployment yet.
        </p>
      </div>
    </div>
  )
}

function SignInGate({ onSignedIn }: { onSignedIn: () => void }) {
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-6 text-center">
        <div className="space-y-2">
          <h1 className="text-xl font-semibold text-foreground">Platform admin console</h1>
          <p className="text-sm text-muted-foreground">
            Sign in with a Raven Automation account holding the Platform Ops role.
          </p>
        </div>
        <button
          disabled={loading}
          onClick={async () => {
            setLoading(true)
            setError(null)
            try {
              await adminLogin()
              onSignedIn()
            } catch {
              setError('Sign-in failed or was cancelled.')
            } finally {
              setLoading(false)
            }
          }}
          className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {loading ? 'Signing in…' : 'Sign in'}
        </button>
        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>
    </div>
  )
}

function StatusBadge({ isActive }: { isActive: boolean }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
        isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
      }`}
    >
      {isActive ? 'Active' : 'Suspended'}
    </span>
  )
}

function ProvisionedBadge({ provisioned }: { provisioned: boolean }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
        provisioned ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
      }`}
    >
      {provisioned ? 'Provisioned' : 'Not provisioned'}
    </span>
  )
}

// Suspend requires typing the tenant's slug back before it's allowed to
// proceed -- activate does not (a suspended tenant re-activating is the
// recoverable direction; suspending a live tenant is the one that needs a
// deliberate, hard-to-fat-finger confirmation).
function SuspendConfirmDialog({
  slug,
  onConfirm,
  onCancel,
  pending,
}: {
  slug: string
  onConfirm: () => void
  onCancel: () => void
  pending: boolean
}) {
  const [typed, setTyped] = useState('')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-sm space-y-4 rounded-lg bg-background p-6 shadow-lg">
        <div className="space-y-1">
          <h2 className="text-base font-semibold text-foreground">Suspend '{slug}'?</h2>
          <p className="text-sm text-muted-foreground">
            Every sign-in for this tenant will be rejected once this takes effect. Type the
            tenant slug below to confirm.
          </p>
        </div>
        <input
          autoFocus
          value={typed}
          onChange={e => setTyped(e.target.value)}
          placeholder={slug}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
          >
            Cancel
          </button>
          <button
            disabled={typed !== slug || pending}
            onClick={onConfirm}
            className="rounded-md bg-destructive px-3 py-2 text-sm font-medium text-destructive-foreground hover:opacity-90 disabled:opacity-50"
          >
            {pending ? 'Suspending…' : 'Suspend'}
          </button>
        </div>
      </div>
    </div>
  )
}

const DEFAULT_GRACE_DAYS = 7 // mirrors adminTenantDeprovision.ts's own default

// The most severe action in this console -- irreversible data destruction,
// scheduled rather than immediate (stage 1: scheduling only, see
// adminTenantDeprovision.ts's own header comment). Typed-slug confirmation
// like SuspendConfirmDialog, plus a visible grace-period countdown so the
// admin sees exactly what they're committing to before confirming, not just
// after.
function DeprovisionConfirmDialog({
  slug,
  onConfirm,
  onCancel,
  pending,
  error,
}: {
  slug: string
  onConfirm: (graceDays: number) => void
  onCancel: () => void
  pending: boolean
  error: string | null
}) {
  const [typed, setTyped] = useState('')
  const [graceDays, setGraceDays] = useState(String(DEFAULT_GRACE_DAYS))
  const graceDaysNum = parseInt(graceDays, 10)
  const validGraceDays = Number.isInteger(graceDaysNum) && graceDaysNum >= 1 && graceDaysNum <= 90

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-sm space-y-4 rounded-lg bg-background p-6 shadow-lg">
        <div className="space-y-1">
          <h2 className="text-base font-semibold text-foreground">Deprovision '{slug}'?</h2>
          <p className="text-sm text-muted-foreground">
            Suspends this tenant immediately and schedules permanent destruction of its Postgres
            schema, SOW documents, and Key Vault secrets after the grace period below. This cannot
            be undone once the grace period elapses. Type the tenant slug below to confirm.
          </p>
        </div>
        <div className="space-y-1">
          <label htmlFor="deprovision-grace-days" className="text-sm font-medium text-foreground">
            Grace period (days)
          </label>
          <input
            id="deprovision-grace-days"
            type="number"
            min={1}
            max={90}
            value={graceDays}
            onChange={e => setGraceDays(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
        </div>
        <input
          autoFocus
          value={typed}
          onChange={e => setTyped(e.target.value)}
          placeholder={slug}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
          >
            Cancel
          </button>
          <button
            disabled={typed !== slug || !validGraceDays || pending}
            onClick={() => onConfirm(graceDaysNum)}
            className="rounded-md bg-destructive px-3 py-2 text-sm font-medium text-destructive-foreground hover:opacity-90 disabled:opacity-50"
          >
            {pending ? 'Scheduling…' : `Schedule deprovision (${validGraceDays ? graceDaysNum : '?'} day grace period)`}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Teardown: stage 2's destructive execution (see adminTenantTeardown.ts
// and quill-bug-log.md's "No tenant deprovisioning or deletion capability
// exists" for the full design). Separate from DeprovisionConfirmDialog
// above, which only SCHEDULES destruction -- these two dialogs are what
// actually preview and perform it, available once a tenant is scheduled. ──

interface TeardownPlanData {
  tenantSlug: string
  schemaExists: boolean
  tableCount: number
  blobs: { path: string; sizeBytes: number }[]
  kvSecretNames: string[]
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// Read-only -- GET .../destroy/preview has no scheduling requirement and
// makes zero destructive calls, so this dialog needs no confirmation gate
// of its own.
function TeardownPreviewDialog({ slug, onClose }: { slug: string; onClose: () => void }) {
  const preview = useQuery({
    queryKey: ['admin-tenant-teardown-preview', slug],
    queryFn: () => adminApi.get<{ data: TeardownPlanData }>(`/admin/tenants/${slug}/destroy/preview`),
  })

  const plan = preview.data?.data

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-lg space-y-4 rounded-lg bg-background p-6 shadow-lg">
        <div className="space-y-1">
          <h2 className="text-base font-semibold text-foreground">Teardown preview: '{slug}'</h2>
          <p className="text-sm text-muted-foreground">
            Exactly what a real teardown would destroy -- enumerated now, nothing deleted.
          </p>
        </div>

        {preview.isLoading && (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {preview.error && (
          <p className="text-sm text-destructive">
            {preview.error instanceof AdminApiError ? preview.error.message : 'Failed to load teardown preview'}
          </p>
        )}

        {plan && (
          <div className="max-h-64 space-y-3 overflow-y-auto rounded-md border border-border p-3 text-sm">
            <div>
              <span className="font-medium text-foreground">Postgres schema: </span>
              {plan.schemaExists ? (
                <span className="text-foreground">exists, {plan.tableCount} table(s)</span>
              ) : (
                <span className="text-muted-foreground">already gone</span>
              )}
            </div>
            <div>
              <span className="font-medium text-foreground">Blob storage: </span>
              {plan.blobs.length === 0 ? (
                <span className="text-muted-foreground">nothing found</span>
              ) : (
                <ul className="mt-1 ml-4 space-y-0.5 text-xs text-muted-foreground">
                  {plan.blobs.map(b => (
                    <li key={b.path} className="font-mono">{b.path} — {formatBytes(b.sizeBytes)}</li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <span className="font-medium text-foreground">Key Vault secrets: </span>
              {plan.kvSecretNames.length === 0 ? (
                <span className="text-muted-foreground">nothing found</span>
              ) : (
                <ul className="mt-1 ml-4 space-y-0.5 text-xs text-muted-foreground">
                  {plan.kvSecretNames.map(n => <li key={n} className="font-mono">{n}</li>)}
                </ul>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

interface TeardownRunResult {
  tenantSlug: string
  dryRun: boolean
  schemaDropped: boolean
  blobPathsDeleted: string[]
  kvSecretsSoftDeleted: string[]
  kvPurgeBlocked: boolean
  errors: string[]
  durationMs: number
  certificateId: string
}

function TeardownResultSummary({ result }: { result: TeardownRunResult }) {
  return (
    <div className="space-y-1.5 rounded-md border border-border p-3 text-sm">
      <p className={result.dryRun ? 'font-medium text-foreground' : 'font-medium text-destructive'}>
        {result.dryRun ? 'Dry run complete — nothing was actually destroyed' : 'REAL teardown complete'}
      </p>
      <p>Schema dropped: {result.schemaDropped ? 'yes' : 'no'}</p>
      <p>Blobs deleted: {result.blobPathsDeleted.length}</p>
      <p>
        Key Vault secrets soft-deleted: {result.kvSecretsSoftDeleted.length}
        {result.kvPurgeBlocked && ' (purge blocked by purge protection — soft-deleted only, see certificate)'}
      </p>
      {result.errors.length > 0 && (
        <div className="text-destructive">
          <p className="font-medium">Errors:</p>
          <ul className="ml-4 list-disc">
            {result.errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}
      <p className="text-xs text-muted-foreground">Certificate: {result.certificateId}</p>
    </div>
  )
}

// Two deliberate steps between "click a button" and irreversible data
// destruction: a dry run (dryRun: true, the only thing this dialog can send
// at first) must complete before the second, separately typed-slug-gated
// "destroy for real" section even renders.
function TeardownRunDialog({ slug, onClose }: { slug: string; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [dryRunTyped, setDryRunTyped] = useState('')
  const [realTyped, setRealTyped] = useState('')

  const run = useMutation({
    mutationFn: (dryRun: boolean) =>
      adminApi.post<{ data: TeardownRunResult }>(`/admin/tenants/${slug}/destroy/run`, { dryRun }),
    onSuccess: (result) => {
      if (!result.data.dryRun) {
        void queryClient.invalidateQueries({ queryKey: ['admin-tenants'] })
      }
    },
  })

  const result = run.data?.data ?? null
  const dryRunDone = result?.dryRun === true
  const realDone = result?.dryRun === false

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-lg space-y-4 rounded-lg bg-background p-6 shadow-lg">
        <div className="space-y-1">
          <h2 className="text-base font-semibold text-foreground">Run destruction: '{slug}'</h2>
          <p className="text-sm text-muted-foreground">
            Step 1 runs a dry run only (nothing destroyed). Step 2, separately confirmed, performs
            the real, irreversible teardown.
          </p>
        </div>

        {!result && (
          <div className="space-y-2">
            <input
              autoFocus
              value={dryRunTyped}
              onChange={e => setDryRunTyped(e.target.value)}
              placeholder={slug}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
            {run.isError && (
              <p className="text-sm text-destructive">
                {run.error instanceof AdminApiError ? run.error.message : 'Dry run failed'}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                className="rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
              >
                Cancel
              </button>
              <button
                disabled={dryRunTyped !== slug || run.isPending}
                onClick={() => run.mutate(true)}
                className="rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
              >
                {run.isPending ? 'Running dry run…' : 'Run dry run'}
              </button>
            </div>
          </div>
        )}

        {dryRunDone && (
          <>
            <TeardownResultSummary result={result} />
            <div className="space-y-2 rounded-md border border-destructive bg-destructive/10 p-3">
              <p className="text-sm font-medium text-destructive">
                Destroy for real — this cannot be undone. Type the tenant slug again to confirm.
              </p>
              <input
                value={realTyped}
                onChange={e => setRealTyped(e.target.value)}
                placeholder={slug}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={onClose}
                  className="rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
                >
                  Close without destroying
                </button>
                <button
                  disabled={realTyped !== slug || run.isPending}
                  onClick={() => run.mutate(false)}
                  className="rounded-md bg-destructive px-3 py-2 text-sm font-medium text-destructive-foreground hover:opacity-90 disabled:opacity-50"
                >
                  {run.isPending ? 'Destroying…' : 'Destroy for real'}
                </button>
              </div>
            </div>
          </>
        )}

        {realDone && (
          <>
            <TeardownResultSummary result={result} />
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
              >
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

const MIN_REASON_LENGTH = 3 // mirrors adminImpersonation.ts's own ImpersonateBodySchema

// Reason is required, not optional -- adminImpersonation.ts writes it into
// the pool-wide audit row before minting anything (fail-closed: no reason,
// no request; no audit row, no token). This dialog exists so that
// requirement has somewhere to be typed, matching SuspendConfirmDialog's
// own fixed-overlay pattern rather than inventing a second one.
function ImpersonateDialog({
  slug,
  onConfirm,
  onCancel,
  pending,
  error,
}: {
  slug: string
  onConfirm: (reason: string) => void
  onCancel: () => void
  pending: boolean
  error: string | null
}) {
  const [reason, setReason] = useState('')
  const trimmed = reason.trim()

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-sm space-y-4 rounded-lg bg-background p-6 shadow-lg">
        <div className="space-y-1">
          <h2 className="text-base font-semibold text-foreground">Impersonate '{slug}'</h2>
          <p className="text-sm text-muted-foreground">
            This opens a new tab, signed in as this tenant with admin access, for up to 30
            minutes. Recorded in the pool-wide impersonation audit log.
          </p>
        </div>
        <div className="space-y-1">
          <label htmlFor="impersonate-reason" className="text-sm font-medium text-foreground">
            Reason
          </label>
          <input
            id="impersonate-reason"
            autoFocus
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. investigating a customer-reported bug"
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
          >
            Cancel
          </button>
          <button
            disabled={trimmed.length < MIN_REASON_LENGTH || pending}
            onClick={() => onConfirm(trimmed)}
            className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {pending ? 'Starting…' : 'Start impersonation'}
          </button>
        </div>
      </div>
    </div>
  )
}

interface ImpersonateResponse {
  token: string
  tenantSlug: string
  schemaName: string
  expiresAt: string
  auditId: string
}

// ── Engagement analytics (Sep 28 rock item) ────────────────────────────────

interface AiUsageSummaryWindow {
  generations: number
  successCount: number
  truncatedCount: number
  errorCount: number
  timeoutCount: number
  totalInputTokens: number
  totalOutputTokens: number
  avgInputTokensPerGeneration: number | null
  avgOutputTokensPerGeneration: number | null
}

interface ProposalCounts {
  currentMonth: number
  previousMonth: number
  lifetime: number
}

interface AiUsageSummary {
  currentMonth: AiUsageSummaryWindow
  previousMonth: AiUsageSummaryWindow
  lifetime: AiUsageSummaryWindow
  avgGenerationsPerMonth: number | null
  // Completed proposals, from the tenant's own schema -- null when the
  // tenant has no schema to read. Distinct from `generations` above, which
  // counts Claude API calls (one wizard proposal is three calls).
  proposals: ProposalCounts | null
}

interface TenantIntegrationSummary {
  name: string
  isEnabled: boolean
  isConfigured: boolean
  lastTestedAt: string | null
  lastTestStatus: string | null
}

function formatCount(n: number): string {
  return n.toLocaleString()
}

function formatAvg(n: number | null): string {
  if (n == null) return '—'
  return n < 10 ? n.toFixed(1) : Math.round(n).toLocaleString()
}

// One column per window -- current month, previous month, lifetime -- so a
// spike or a drop-off is visible at a glance without cross-referencing three
// separate calls. Failed here means truncated+error+timeout together: a
// truncation still cost real tokens the same way an error or a clean success
// did, so lumping them as "not successful" is the number Platform Ops
// actually wants for cost review, with the breakdown still available per
// column for a "why" follow-up.
function UsageWindowColumn({
  label,
  window,
  proposals,
}: {
  label: string
  window: AiUsageSummaryWindow
  proposals: number | null
}) {
  const failed = window.truncatedCount + window.errorCount + window.timeoutCount
  return (
    <div className="space-y-1.5 rounded-md border border-border p-3">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-semibold text-foreground">{proposals == null ? '—' : formatCount(proposals)}</span>
        <span className="text-xs text-muted-foreground">proposals</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-sm font-medium text-foreground">{formatCount(window.generations)}</span>
        <span className="text-xs text-muted-foreground">AI calls</span>
      </div>
      <div className="flex gap-3 text-xs">
        <span className="text-emerald-700">{formatCount(window.successCount)} succeeded</span>
        {failed > 0 && <span className="text-destructive">{formatCount(failed)} failed</span>}
      </div>
      <div className="border-t border-border pt-1.5 text-xs text-muted-foreground">
        <div>in: {formatCount(window.totalInputTokens)} tok (avg {formatAvg(window.avgInputTokensPerGeneration)})</div>
        <div>out: {formatCount(window.totalOutputTokens)} tok (avg {formatAvg(window.avgOutputTokensPerGeneration)})</div>
      </div>
    </div>
  )
}

function AnalyticsSection({ slug }: { slug: string }) {
  const usage = useQuery({
    queryKey: ['admin-tenant-usage-summary', slug],
    queryFn: () => adminApi.get<{ data: AiUsageSummary }>(`/admin/tenants/${slug}/usage/summary`),
  })
  const integrations = useQuery({
    queryKey: ['admin-tenant-integrations', slug],
    queryFn: () => adminApi.get<{ data: TenantIntegrationSummary[] }>(`/admin/tenants/${slug}/integrations`),
  })

  return (
    <div className="space-y-4 border-t border-border pt-4">
      <div className="flex items-center gap-2">
        <BarChart3 className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-medium text-foreground">AI usage &amp; cost</h3>
      </div>

      {usage.isLoading && (
        <div className="flex justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {usage.error && (
        <p className="text-sm text-destructive">
          {usage.error instanceof AdminApiError ? usage.error.message : 'Failed to load usage data'}
        </p>
      )}

      {usage.data && (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <UsageWindowColumn label="This month" window={usage.data.data.currentMonth} proposals={usage.data.data.proposals?.currentMonth ?? null} />
            <UsageWindowColumn label="Last month" window={usage.data.data.previousMonth} proposals={usage.data.data.proposals?.previousMonth ?? null} />
            <UsageWindowColumn label="Lifetime" window={usage.data.data.lifetime} proposals={usage.data.data.proposals?.lifetime ?? null} />
          </div>
          <p className="text-xs text-muted-foreground">
            Proposals are counted when the New Proposal wizard completes. A wizard proposal makes 3 AI calls;
            "Generate with AI" on a saved proposal makes 2. Averages {formatAvg(usage.data.data.avgGenerationsPerMonth)} AI calls/month since first use.
          </p>
        </>
      )}

      <div className="flex items-center gap-2 pt-2">
        <Plug className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-medium text-foreground">Configured integrations</h3>
      </div>

      {integrations.isLoading && (
        <div className="flex justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {integrations.error && (
        <p className="text-sm text-destructive">
          {integrations.error instanceof AdminApiError ? integrations.error.message : 'Failed to load integrations'}
        </p>
      )}

      {integrations.data && (
        integrations.data.data.length === 0 ? (
          <p className="text-sm text-muted-foreground">No integrations configured.</p>
        ) : (
          <ul className="space-y-1.5">
            {integrations.data.data.map(i => (
              <li key={i.name} className="flex items-center justify-between rounded-md border border-border px-3 py-1.5 text-sm">
                <span className="font-medium capitalize text-foreground">{i.name}</span>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                    i.isConfigured && i.isEnabled
                      ? 'bg-emerald-100 text-emerald-700'
                      : i.isConfigured
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {i.isConfigured && i.isEnabled ? 'Enabled' : i.isConfigured ? 'Configured, disabled' : 'Not configured'}
                </span>
              </li>
            ))}
          </ul>
        )
      )}
    </div>
  )
}

// ── Per-tenant migration (Platform Ops' own path -- see adminTenantMigration.ts's
// header comment for why the existing self-serve tenant endpoint can't be
// used from here, impersonation included) ──────────────────────────────────

interface TenantMigrationPreview {
  tenantSlug: string
  applied: string[]
  pending: string[]
}

interface TenantMigrationResult {
  tenantSlug: string
  schema: string
  applied: string[]
  skipped: string[]
}

function MigrationSection({ slug, provisioned }: { slug: string; provisioned: boolean }) {
  const queryClient = useQueryClient()
  const preview = useQuery({
    queryKey: ['admin-tenant-migrate-preview', slug],
    queryFn: () => adminApi.get<{ data: TenantMigrationPreview }>(`/admin/tenants/${slug}/migrate/preview`),
    enabled: provisioned,
  })

  const run = useMutation({
    mutationFn: () => adminApi.post<{ data: TenantMigrationResult }>(`/admin/tenants/${slug}/migrate`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-tenant-migrate-preview', slug] })
      void queryClient.invalidateQueries({ queryKey: ['admin-tenants'] })
    },
  })

  if (!provisioned) {
    return (
      <div className="border-t border-border pt-4">
        <div className="flex items-center gap-2">
          <DatabaseZap className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-medium text-foreground">Migrations</h3>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          No schema yet -- this tenant's first migration run creates it. Use "Run migration"
          below once you're ready to provision.
        </p>
        <button
          disabled={run.isPending}
          onClick={() => run.mutate()}
          className="mt-2 flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-muted disabled:opacity-50"
        >
          <DatabaseZap className="h-4 w-4" />
          {run.isPending ? 'Running…' : 'Run migration'}
        </button>
        {run.isError && (
          <p className="mt-2 text-sm text-destructive">
            {run.error instanceof AdminApiError ? run.error.message : 'Migration failed'}
          </p>
        )}
        {run.isSuccess && (
          <p className="mt-2 text-sm text-emerald-700">
            Applied {run.data.data.applied.length} migration(s).
          </p>
        )}
      </div>
    )
  }

  const pendingCount = preview.data?.data.pending.length ?? 0

  return (
    <div className="border-t border-border pt-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <DatabaseZap className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-medium text-foreground">Migrations</h3>
        </div>
        {!preview.isLoading && !preview.error && (
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-medium ${
              pendingCount > 0 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
            }`}
          >
            {pendingCount > 0 ? `${pendingCount} pending` : 'Up to date'}
          </span>
        )}
      </div>

      {preview.isLoading && (
        <div className="mt-2 flex justify-center py-2">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      )}

      {preview.error && (
        <p className="mt-2 text-sm text-destructive">
          {preview.error instanceof AdminApiError ? preview.error.message : 'Failed to check migration status'}
        </p>
      )}

      {pendingCount > 0 && (
        <ul className="mt-2 space-y-0.5 text-xs text-muted-foreground">
          {preview.data!.data.pending.map(f => <li key={f} className="font-mono">{f}</li>)}
        </ul>
      )}

      <button
        disabled={run.isPending || pendingCount === 0}
        onClick={() => run.mutate()}
        className="mt-2 flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-muted disabled:opacity-50 disabled:hover:bg-transparent"
      >
        <DatabaseZap className="h-4 w-4" />
        {run.isPending ? 'Running…' : 'Run migration'}
      </button>

      {run.isError && (
        <p className="mt-2 text-sm text-destructive">
          {run.error instanceof AdminApiError ? run.error.message : 'Migration failed'}
        </p>
      )}
      {run.isSuccess && (
        <p className="mt-2 text-sm text-emerald-700">
          Applied {run.data.data.applied.length} migration(s).
        </p>
      )}
    </div>
  )
}

function TenantDetail({ tenant }: { tenant: AdminTenantSummary }) {
  const queryClient = useQueryClient()
  const [confirmingSuspend, setConfirmingSuspend] = useState(false)
  const [lifecycleError, setLifecycleError] = useState<string | null>(null)
  const [confirmingImpersonate, setConfirmingImpersonate] = useState(false)
  const [confirmingDeprovision, setConfirmingDeprovision] = useState(false)
  const [previewingTeardown, setPreviewingTeardown] = useState(false)
  const [runningTeardown, setRunningTeardown] = useState(false)

  const lifecycle = useMutation({
    mutationFn: (action: 'activate' | 'suspend') =>
      adminApi.post<{ data: AdminTenantSummary }>(`/admin/tenants/${tenant.slug}/lifecycle`, { action }),
    onSuccess: () => {
      setLifecycleError(null)
      setConfirmingSuspend(false)
      void queryClient.invalidateQueries({ queryKey: ['admin-tenants'] })
    },
    onError: (err) => {
      setLifecycleError(err instanceof AdminApiError ? err.message : 'Lifecycle change failed')
    },
  })

  const impersonate = useMutation({
    mutationFn: (reason: string) =>
      adminApi.post<{ data: ImpersonateResponse }>(`/admin/tenants/${tenant.slug}/impersonate`, { reason }),
    onSuccess: (result) => {
      setConfirmingImpersonate(false)
      const session: ImpersonationSession = {
        token: result.data.token,
        tenantSlug: result.data.tenantSlug,
        adminEmail: getAdminAccount()?.username ?? 'unknown admin',
        expiresAt: result.data.expiresAt,
      }
      // New tab, not the current one: this stays the admin console tab,
      // still signed in as Platform Ops, so more than one impersonation
      // session (this tenant or another) can be started without losing
      // that context. window.open (not <a target=_blank>) because the URL
      // itself doesn't exist until the token comes back from this mutation.
      window.open(`${window.location.origin}/impersonate#${encodeImpersonationPayload(session)}`, '_blank')
    },
    // Deliberately no onError setting a dialog-level error string here --
    // ImpersonateDialog reads impersonate.error directly (see below) so it
    // doesn't go stale if this component re-renders between the mutation
    // settling and the dialog closing.
  })

  const deprovision = useMutation({
    mutationFn: (graceDays: number) =>
      adminApi.post<{ data: AdminTenantSummary }>(`/admin/tenants/${tenant.slug}/deprovision`, { graceDays }),
    onSuccess: () => {
      setConfirmingDeprovision(false)
      void queryClient.invalidateQueries({ queryKey: ['admin-tenants'] })
    },
  })

  const cancelDeprovision = useMutation({
    mutationFn: () => adminApi.post<{ data: AdminTenantSummary }>(`/admin/tenants/${tenant.slug}/deprovision/cancel`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-tenants'] })
    },
  })

  // A torn-down tenant gets its own minimal, read-mostly view -- suspend/
  // activate, impersonate, usage analytics, and schema migrations are all
  // meaningless (or actively misleading) once the schema is gone, so none
  // of that renders here. Preview/run stay available, since re-checking or
  // re-running teardown after a prior partial failure is still a real,
  // supported action. See TenantList below for where this tenant is listed
  // -- moved out of the main table entirely, into its own section.
  if (tenant.deprovisionedAt) {
    return (
      <div className="space-y-6 rounded-lg border border-border p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">{tenant.slug}</h2>
            <div className="mt-1 flex gap-2">
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                Deprovisioned
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-start gap-2 rounded-md border border-border bg-muted p-3 text-sm text-muted-foreground">
          <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            Torn down on <strong>{formatDate(tenant.deprovisionedAt)}</strong>, originally scheduled by{' '}
            {tenant.deprovisionInitiatedBy ?? 'unknown admin'}. Its Postgres schema, SOW documents, and Key Vault
            secrets were destroyed -- Key Vault secrets are soft-deleted only (purge protection), see the deletion
            certificate for the full record.
          </p>
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <dt className="text-muted-foreground">Entra tenant ID</dt>
          <dd className="font-mono text-foreground">{tenant.entraTenantId}</dd>
          <dt className="text-muted-foreground">Entra client ID</dt>
          <dd className="font-mono text-foreground">{tenant.entraClientId}</dd>
          <dt className="text-muted-foreground">Schema (destroyed)</dt>
          <dd className="font-mono text-foreground">{tenant.schemaName}</dd>
        </dl>

        <div className="border-t border-border pt-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-medium text-foreground">Re-check teardown</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Preview re-enumerates live state -- useful to confirm nothing was left behind after a prior
                partial failure. Running again is a safe no-op once everything is already gone.
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button
                onClick={() => setPreviewingTeardown(true)}
                className="rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-muted"
              >
                Preview
              </button>
              <button
                onClick={() => setRunningTeardown(true)}
                className="flex items-center gap-2 rounded-md border border-destructive px-3 py-2 text-sm font-medium text-destructive hover:bg-destructive/10"
              >
                <Eraser className="h-4 w-4" />
                Run again
              </button>
            </div>
          </div>
        </div>

        {previewingTeardown && (
          <TeardownPreviewDialog slug={tenant.slug} onClose={() => setPreviewingTeardown(false)} />
        )}

        {runningTeardown && (
          <TeardownRunDialog slug={tenant.slug} onClose={() => setRunningTeardown(false)} />
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6 rounded-lg border border-border p-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">{tenant.slug}</h2>
          <div className="mt-1 flex gap-2">
            <StatusBadge isActive={tenant.isActive} />
            <ProvisionedBadge provisioned={tenant.provisioned} />
          </div>
        </div>
      </div>

      {tenant.pendingDeletionAt && (
        <div className="flex items-start justify-between gap-3 rounded-md border border-destructive bg-destructive/10 p-3 text-sm">
          <div className="flex items-start gap-2">
            <CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
            <p className="text-destructive">
              Scheduled for permanent deletion on <strong>{formatDate(tenant.pendingDeletionAt)}</strong>, requested by{' '}
              {tenant.deprovisionInitiatedBy ?? 'unknown admin'}. Its Postgres schema, SOW documents, and Key
              Vault secrets will be destroyed then -- this cannot be undone once it happens.
            </p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1.5">
            <button
              disabled={cancelDeprovision.isPending}
              onClick={() => cancelDeprovision.mutate()}
              className="rounded-md border border-destructive px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10 disabled:opacity-50"
            >
              {cancelDeprovision.isPending ? 'Cancelling…' : 'Cancel deprovisioning'}
            </button>
            <button
              onClick={() => setPreviewingTeardown(true)}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
            >
              Preview destruction (dry run)
            </button>
            <button
              onClick={() => setRunningTeardown(true)}
              className="flex items-center gap-1.5 rounded-md bg-destructive px-3 py-1.5 text-xs font-medium text-destructive-foreground hover:opacity-90"
            >
              <Eraser className="h-3.5 w-3.5" />
              Run destruction now
            </button>
          </div>
        </div>
      )}
      {cancelDeprovision.isError && (
        <p className="text-sm text-destructive">
          {cancelDeprovision.error instanceof AdminApiError ? cancelDeprovision.error.message : 'Failed to cancel'}
        </p>
      )}

      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <dt className="text-muted-foreground">Entra tenant ID</dt>
        <dd className="font-mono text-foreground">{tenant.entraTenantId}</dd>
        <dt className="text-muted-foreground">Entra client ID</dt>
        <dd className="font-mono text-foreground">{tenant.entraClientId}</dd>
        <dt className="text-muted-foreground">Schema</dt>
        <dd className="font-mono text-foreground">{tenant.schemaName}</dd>
        <dt className="text-muted-foreground">Public share slug</dt>
        <dd className="font-mono text-foreground">{tenant.publicSlug ?? '(not backfilled)'}</dd>
      </dl>

      {/* Correction 5: suspension is honest about its own latency -- it
          propagates on the tenant registry's 5-minute per-instance cache
          TTL (withAuthPooled.ts -> tenantRegistry.ts), not immediately.
          No cache invalidation is added in this pass; the UI states the
          delay instead of implying an instant effect. */}
      <div className="flex items-start gap-2 rounded-md bg-muted p-3 text-sm text-muted-foreground">
        <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <p>
          A suspend or reactivate below takes effect within about 5 minutes -- every Function
          App instance caches this tenant's registry row for that long before checking again.
        </p>
      </div>

      {tenant.isActive ? (
        <button
          onClick={() => setConfirmingSuspend(true)}
          className="flex items-center gap-2 rounded-md border border-destructive px-3 py-2 text-sm font-medium text-destructive hover:bg-destructive/10"
        >
          <PowerOff className="h-4 w-4" />
          Suspend tenant
        </button>
      ) : (
        <div className="space-y-2">
          {/* Correction 7: a suspended tenant is skipped by any
              listAllTenants-driven migrate sweep -- reactivating it can
              expose a schema that's now behind the deployed code. Shown as
              a standing warning, not a confirmation gate: activate itself
              needs no typed confirmation (correction 6), this is
              informational only. */}
          <div className="flex items-start gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-800">
            <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              While suspended, this tenant was skipped by any registry-driven migration sweep.
              If migrations shipped during the suspension, its schema may now be behind the
              deployed code -- consider re-running the migrate step after reactivating.
            </p>
          </div>
          <button
            onClick={() => lifecycle.mutate('activate')}
            disabled={lifecycle.isPending}
            className="flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            <Power className="h-4 w-4" />
            {lifecycle.isPending ? 'Activating…' : 'Activate tenant'}
          </button>
        </div>
      )}

      {lifecycleError && <p className="text-sm text-destructive">{lifecycleError}</p>}

      <div className="border-t border-border pt-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-foreground">Impersonate as tenant admin</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Opens a new tab, signed in as this tenant, for up to 30 minutes. Every session is
              recorded in the pool-wide impersonation audit log.
            </p>
          </div>
          <button
            disabled={!tenant.isActive || !tenant.provisioned}
            title={
              !tenant.isActive ? 'Suspended tenants cannot be impersonated'
                : !tenant.provisioned ? 'Tenant has no schema yet -- nothing to impersonate into'
                : undefined
            }
            onClick={() => setConfirmingImpersonate(true)}
            className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-muted disabled:opacity-50 disabled:hover:bg-transparent"
          >
            <UserCog className="h-4 w-4" />
            Impersonate
          </button>
        </div>
      </div>

      <AnalyticsSection slug={tenant.slug} />

      <MigrationSection slug={tenant.slug} provisioned={tenant.provisioned} />

      {!tenant.pendingDeletionAt && (
        <div className="border-t border-border pt-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-destructive">Deprovision tenant</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Schedules permanent destruction of this tenant's data after a grace period.
                Irreversible once the grace period elapses.
              </p>
            </div>
            <button
              onClick={() => setConfirmingDeprovision(true)}
              className="flex shrink-0 items-center gap-2 rounded-md border border-destructive px-3 py-2 text-sm font-medium text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4" />
              Deprovision
            </button>
          </div>
        </div>
      )}

      {confirmingSuspend && (
        <SuspendConfirmDialog
          slug={tenant.slug}
          pending={lifecycle.isPending}
          onCancel={() => setConfirmingSuspend(false)}
          onConfirm={() => lifecycle.mutate('suspend')}
        />
      )}

      {confirmingImpersonate && (
        <ImpersonateDialog
          slug={tenant.slug}
          pending={impersonate.isPending}
          error={impersonate.error instanceof AdminApiError ? impersonate.error.message : null}
          onCancel={() => setConfirmingImpersonate(false)}
          onConfirm={(reason) => impersonate.mutate(reason)}
        />
      )}

      {confirmingDeprovision && (
        <DeprovisionConfirmDialog
          slug={tenant.slug}
          pending={deprovision.isPending}
          error={deprovision.error instanceof AdminApiError ? deprovision.error.message : null}
          onCancel={() => setConfirmingDeprovision(false)}
          onConfirm={(graceDays) => deprovision.mutate(graceDays)}
        />
      )}

      {previewingTeardown && (
        <TeardownPreviewDialog slug={tenant.slug} onClose={() => setPreviewingTeardown(false)} />
      )}

      {runningTeardown && (
        <TeardownRunDialog slug={tenant.slug} onClose={() => setRunningTeardown(false)} />
      )}
    </div>
  )
}

function TenantList() {
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-tenants'],
    queryFn: () => adminApi.get<{ data: AdminTenantSummary[] }>('/admin/tenants'),
  })

  const tenants = data?.data ?? []
  // Deprovisioned tenants get their own, separate section (see
  // TenantDetail's own branch above for why) -- kept out of the main table
  // entirely so support staff aren't scanning past torn-down rows that no
  // longer support most of the actions a live tenant does.
  const liveTenants = tenants.filter(t => !t.deprovisionedAt)
  const deprovisionedTenants = tenants.filter(t => t.deprovisionedAt)
  const selected = tenants.find(t => t.slug === selectedSlug) ?? null

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return (
      <p className="text-sm text-destructive">
        {error instanceof AdminApiError ? error.message : 'Failed to load tenants'}
      </p>
    )
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="space-y-4">
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-2">Tenant</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Provisioning</th>
              </tr>
            </thead>
            <tbody>
              {liveTenants.map(t => (
                <tr
                  key={t.slug}
                  onClick={() => setSelectedSlug(t.slug)}
                  className={`cursor-pointer border-t border-border hover:bg-muted/50 ${
                    selectedSlug === t.slug ? 'bg-muted/50' : ''
                  }`}
                >
                  <td className="px-4 py-2 font-medium text-foreground">{t.slug}</td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-1.5">
                      <StatusBadge isActive={t.isActive} />
                      {t.pendingDeletionAt && (
                        <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive">
                          Pending deletion
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2"><ProvisionedBadge provisioned={t.provisioned} /></td>
                </tr>
              ))}
              {liveTenants.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-4 py-6 text-center text-muted-foreground">
                    No active tenants in the registry.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {deprovisionedTenants.length > 0 && (
          <details className="overflow-hidden rounded-lg border border-border">
            <summary className="cursor-pointer select-none bg-muted px-4 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground hover:bg-muted/70">
              Deprovisioned tenants ({deprovisionedTenants.length})
            </summary>
            <table className="w-full text-sm">
              <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-2">Tenant</th>
                  <th className="px-4 py-2">Torn down</th>
                  <th className="px-4 py-2">Initiated by</th>
                </tr>
              </thead>
              <tbody>
                {deprovisionedTenants.map(t => (
                  <tr
                    key={t.slug}
                    onClick={() => setSelectedSlug(t.slug)}
                    className={`cursor-pointer border-t border-border hover:bg-muted/50 ${
                      selectedSlug === t.slug ? 'bg-muted/50' : ''
                    }`}
                  >
                    <td className="px-4 py-2 font-medium text-foreground">{t.slug}</td>
                    <td className="px-4 py-2 text-muted-foreground">{formatDate(t.deprovisionedAt!)}</td>
                    <td className="px-4 py-2 text-muted-foreground">{t.deprovisionInitiatedBy ?? 'unknown admin'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        )}
      </div>

      {selected ? (
        <TenantDetail tenant={selected} />
      ) : (
        <div className="flex h-full min-h-[12rem] items-center justify-center rounded-lg border border-dashed border-border text-sm text-muted-foreground">
          Select a tenant to see details
        </div>
      )}
    </div>
  )
}

// ── Migrate all tenants ─────────────────────────────────────────────────────
// Required to preview before it can run anything -- this touches every
// provisioned tenant's schema in one action, so the same caution level as
// impersonation's audit-then-mint sequencing applies: see it before you
// trigger it, not just a plain "are you sure?".

// ── Pool-scoped migration ───────────────────────────────────────────────────
// Separate from per-tenant migrations (MigrateAllDialog below) -- this runs
// against the shared `public` schema, never any one tenant's data (see
// poolMigration.ts's own header comment for why it's gated by withAdminAuth
// and deliberately never auto-invoked by CI). No preview endpoint exists for
// this one: pool-scoped migrations are additive and idempotent (tracked in
// the same quill_schema_migrations table per-tenant migrations use, under
// the '__pool__' sentinel, ON CONFLICT DO NOTHING), so running it is safe to
// repeat and needs no typed confirmation, consistent with "Migrate all
// tenants" below.

interface PoolMigrationResult {
  schema: string
  applied: string[]
  skipped: string[]
}

function PoolMigrateDialog({ onClose }: { onClose: () => void }) {
  const run = useMutation({
    mutationFn: () => adminApi.post<{ data: PoolMigrationResult }>('/admin/pool/migrate', {}),
  })

  const result = run.data?.data ?? null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-md space-y-4 rounded-lg bg-background p-6 shadow-lg">
        <div className="space-y-1">
          <h2 className="text-base font-semibold text-foreground">Migrate pool</h2>
          <p className="text-sm text-muted-foreground">
            Applies every pending pool-scoped migration (shared <code>public</code> schema, not any
            one tenant's data) -- run this once per deploy that ships a new one, such as the
            tenant-deletion-certificates table deprovisioning needs. Safe to run again; already-applied
            migrations are skipped.
          </p>
        </div>

        {result && (
          <div className="space-y-2 text-sm">
            <div>
              <span className="font-medium text-foreground">Applied ({result.applied.length}): </span>
              {result.applied.length === 0 ? (
                <span className="text-muted-foreground">none</span>
              ) : (
                <ul className="mt-1 ml-4 space-y-0.5 text-xs text-muted-foreground">
                  {result.applied.map(f => <li key={f} className="font-mono">{f}</li>)}
                </ul>
              )}
            </div>
            <div>
              <span className="font-medium text-foreground">Already applied ({result.skipped.length}): </span>
              {result.skipped.length === 0 ? (
                <span className="text-muted-foreground">none</span>
              ) : (
                <span className="text-muted-foreground">{result.skipped.length} migration(s) skipped</span>
              )}
            </div>
          </div>
        )}

        {run.isError && (
          <p className="text-sm text-destructive">
            {run.error instanceof AdminApiError ? run.error.message : 'Pool migration failed'}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
          >
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button
              disabled={run.isPending}
              onClick={() => run.mutate()}
              className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {run.isPending ? 'Running…' : 'Run pool migration'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

interface MigrateAllPreviewEntry {
  slug: string
  pending: string[]
  applied: string[]
}

interface MigrateAllRunEntry {
  slug: string
  applied: string[]
  skipped: string[]
  error?: string
}

// Raven admins only. The real reason and dates are shown here and nowhere else: tenants only ever
// see that an address "can't currently receive email" (docs/rfc/email.md, 5.1 and R9).
export interface SuppressionRecord {
  email: string
  stream: string
  reason: 'HardBounce' | 'SpamComplaint' | 'ManualSuppression' | 'Unknown'
  suppressedAt: string
  lastEventAt: string
  reactivatedAt: string | null
  canReactivate: boolean
}

export function EmailSuppressionsDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient()
  const [lookup, setLookup] = useState('')
  const [submitted, setSubmitted] = useState('')
  const list = useQuery({
    queryKey: ['admin-email-suppressions', submitted],
    queryFn: () =>
      adminApi.get<{ data: { stream: string; current: SuppressionRecord[]; match: SuppressionRecord | null } }>(
        `/admin/email/suppressions${submitted ? `?email=${encodeURIComponent(submitted)}` : ''}`
      ),
  })
  const reactivate = useMutation({
    mutationFn: (email: string) => adminApi.post<{ data: { reactivated: boolean } }>('/admin/email/suppressions/reactivate', { email }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-email-suppressions'] }),
  })

  const current = list.data?.data.current ?? []
  const match = list.data?.data.match ?? null
  const row = (r: SuppressionRecord) => (
    <li key={r.email} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 text-sm">
      <div className="min-w-0">
        <p className="truncate font-medium text-foreground">{r.email}</p>
        <p className="text-xs text-muted-foreground">
          {r.reason} · since {formatDate(r.suppressedAt)} · last event {formatDate(r.lastEventAt)}
          {r.reactivatedAt ? ` · reactivated ${formatDate(r.reactivatedAt)}` : ''}
        </p>
      </div>
      {r.reactivatedAt ? (
        <span className="text-xs text-muted-foreground">Not suppressed</span>
      ) : r.canReactivate ? (
        <button
          onClick={() => { if (window.confirm(`Reactivate ${r.email}? Postmark will accept mail to this address again.`)) reactivate.mutate(r.email) }}
          disabled={reactivate.isPending}
          className="rounded-md border px-3 py-1 text-xs font-medium hover:bg-muted disabled:opacity-50"
        >
          Reactivate
        </button>
      ) : (
        <span className="max-w-[16rem] text-xs text-amber-700">Not reactivatable: only Postmark support can lift a spam-complaint suppression.</span>
      )}
    </li>
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-2xl space-y-4 rounded-lg bg-background p-6 shadow-lg">
        <div className="space-y-1">
          <h2 className="text-base font-semibold text-foreground">Email suppressions</h2>
          <p className="text-sm text-muted-foreground">
            Addresses Postmark will not send to (hard bounces, spam complaints, manual suppressions), shared by every tenant.
            Tenants are only told that an address can't currently receive email.
          </p>
        </div>
        <form onSubmit={e => { e.preventDefault(); setSubmitted(lookup.trim()) }} className="flex gap-2">
          <input
            value={lookup}
            onChange={e => setLookup(e.target.value)}
            placeholder="Look up an address"
            aria-label="Look up an address"
            className="flex-1 rounded-md border bg-background px-3 py-2 text-sm"
          />
          <button type="submit" className="rounded-md border px-3 py-2 text-sm hover:bg-muted">Look up</button>
        </form>
        {list.isLoading && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
        {list.isError && <p className="text-sm text-destructive">Could not load suppressions. If the pool migration has not been run, run it first.</p>}
        {reactivate.isError && <p role="alert" className="text-sm text-destructive">{reactivate.error instanceof Error ? reactivate.error.message : 'Reactivation failed'}</p>}
        {reactivate.isSuccess && <p role="status" className="text-sm text-emerald-700">Reactivated.</p>}
        {submitted && !list.isLoading && (
          match
            ? <ul className="divide-y rounded-md border" aria-label="Lookup result">{row(match)}</ul>
            : <p className="text-sm text-muted-foreground">No suppression is recorded for that address.</p>
        )}
        <div>
          <h3 className="mb-1 text-sm font-medium text-foreground">Current suppressions ({current.length})</h3>
          {current.length === 0
            ? <p className="text-sm text-muted-foreground">None.</p>
            : <ul className="max-h-72 divide-y overflow-y-auto rounded-md border" aria-label="Current suppressions">{current.map(row)}</ul>}
        </div>
        <div className="flex justify-end">
          <button onClick={onClose} className="rounded-md border px-4 py-2 text-sm hover:bg-muted">Close</button>
        </div>
      </div>
    </div>
  )
}

export interface TenantEmailEnablement {
  tenantSlug: string
  enabled: boolean
  isDefault: boolean
  updatedAt: string | null
  updatedBy: string | null
}

export function EmailEnablementDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient()
  const list = useQuery({
    queryKey: ['admin-email-enablement'],
    queryFn: () => adminApi.get<{ data: { gating: boolean; tenants: TenantEmailEnablement[] } }>('/admin/email/tenant-enablement'),
  })
  const toggle = useMutation({
    mutationFn: (v: { slug: string; enabled: boolean }) =>
      adminApi.put<{ data: TenantEmailEnablement }>(`/admin/tenants/${v.slug}/email-enabled`, { enabled: v.enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-email-enablement'] }),
  })

  const gating = list.data?.data.gating ?? true
  const tenants = list.data?.data.tenants ?? []

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-2xl space-y-4 rounded-lg bg-background p-6 shadow-lg">
        <div className="space-y-1">
          <h2 className="text-base font-semibold text-foreground">Tenant email</h2>
          <p className="text-sm text-muted-foreground">
            Which tenants may send email. A tenant is on unless you turn it off, and a new tenant is on by default. A change reaches every server within a minute.
          </p>
        </div>
        {list.data && !gating && (
          <p role="note" className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            Per-tenant switching is not active in this environment, so every tenant can send. These switches take effect when it is turned on.
          </p>
        )}
        {list.isLoading && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
        {list.isError && <p className="text-sm text-destructive">Could not load tenant email settings. If the pool migration has not been run, run it first.</p>}
        {toggle.isError && <p role="alert" className="text-sm text-destructive">{toggle.error instanceof Error ? toggle.error.message : 'The change failed'}</p>}
        {list.data && <ul className="max-h-80 divide-y overflow-y-auto rounded-md border" aria-label="Tenant email settings">
          {tenants.map(t => (
            <li key={t.tenantSlug} className="flex items-center justify-between gap-2 px-3 py-2.5 text-sm">
              <div className="min-w-0">
                <p className="truncate font-medium text-foreground">{t.tenantSlug}</p>
                <p className="text-xs text-muted-foreground">
                  {t.enabled ? (t.isDefault ? 'On (default)' : 'On') : 'Off'}
                  {t.updatedAt ? ` · changed ${formatDate(t.updatedAt)} by ${t.updatedBy ?? 'unknown'}` : ''}
                </p>
              </div>
              <button
                onClick={() => {
                  const verb = t.enabled ? 'Turn email off for' : 'Turn email on for'
                  if (window.confirm(`${verb} ${t.tenantSlug}?`)) toggle.mutate({ slug: t.tenantSlug, enabled: !t.enabled })
                }}
                disabled={toggle.isPending}
                className="rounded-md border px-3 py-1 text-xs font-medium hover:bg-muted disabled:opacity-50"
              >
                {t.enabled ? 'Turn off' : 'Turn on'}
              </button>
            </li>
          ))}
        </ul>}
        <div className="flex justify-end">
          <button onClick={onClose} className="rounded-md border px-4 py-2 text-sm hover:bg-muted">Close</button>
        </div>
      </div>
    </div>
  )
}

function MigrateAllDialog({ onClose }: { onClose: () => void }) {
  const preview = useQuery({
    queryKey: ['admin-migrate-all-preview'],
    queryFn: () => adminApi.get<{ data: MigrateAllPreviewEntry[] }>('/admin/tenants/migrate-all/preview'),
  })

  const run = useMutation({
    mutationFn: () => adminApi.post<{ data: MigrateAllRunEntry[] }>('/admin/tenants/migrate-all', {}),
  })

  const entries = preview.data?.data ?? []
  const withPending = entries.filter(e => e.pending.length > 0)
  const results = run.data?.data ?? null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-lg space-y-4 rounded-lg bg-background p-6 shadow-lg">
        <div className="space-y-1">
          <h2 className="text-base font-semibold text-foreground">Migrate all tenants</h2>
          <p className="text-sm text-muted-foreground">
            Runs every pending tenant migration against every provisioned tenant, one at a time.
            Unprovisioned tenants are skipped -- migrate them individually first.
          </p>
        </div>

        {!results && (
          <>
            {preview.isLoading && (
              <div className="flex justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}

            {preview.error && (
              <p className="text-sm text-destructive">
                {preview.error instanceof AdminApiError ? preview.error.message : 'Failed to load preview'}
              </p>
            )}

            {preview.data && (
              withPending.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  All {entries.length} provisioned tenant(s) are already up to date. Nothing to run.
                </p>
              ) : (
                <div className="max-h-64 space-y-2 overflow-y-auto rounded-md border border-border p-3">
                  {withPending.map(e => (
                    <div key={e.slug} className="text-sm">
                      <span className="font-medium text-foreground">{e.slug}</span>
                      <span className="text-muted-foreground"> — {e.pending.length} pending</span>
                      <ul className="ml-4 text-xs text-muted-foreground">
                        {e.pending.map(f => <li key={f} className="font-mono">{f}</li>)}
                      </ul>
                    </div>
                  ))}
                </div>
              )
            )}

            {run.isError && (
              <p className="text-sm text-destructive">
                {run.error instanceof AdminApiError ? run.error.message : 'Migration sweep failed'}
              </p>
            )}
          </>
        )}

        {results && (
          <div className="max-h-64 space-y-2 overflow-y-auto rounded-md border border-border p-3">
            {results.map(r => (
              <div key={r.slug} className="text-sm">
                <span className="font-medium text-foreground">{r.slug}</span>
                {r.error ? (
                  <span className="text-destructive"> — failed: {r.error}</span>
                ) : (
                  <span className="text-emerald-700"> — applied {r.applied.length}</span>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
          >
            {results ? 'Close' : 'Cancel'}
          </button>
          {!results && (
            <button
              disabled={withPending.length === 0 || run.isPending || preview.isLoading}
              onClick={() => run.mutate()}
              className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {run.isPending ? 'Running…' : `Run migration on ${withPending.length} tenant(s)`}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default function AdminConsole() {
  const [signedIn, setSignedIn] = useState(() => Boolean(getAdminAccount()))
  const [migratingAll, setMigratingAll] = useState(false)
  const [showSuppressions, setShowSuppressions] = useState(false)
  const [showEnablement, setShowEnablement] = useState(false)
  const [migratingPool, setMigratingPool] = useState(false)
  const [showBackfills, setShowBackfills] = useState(false)

  if (!isAdminConsoleConfigured()) {
    return <NotConfigured />
  }

  if (!signedIn) {
    return <SignInGate onSignedIn={() => setSignedIn(true)} />
  }

  const account = getAdminAccount()

  return (
    <div className="min-h-screen bg-background px-6 py-8">
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-foreground">Pool admin console</h1>
            <p className="text-sm text-muted-foreground">
              {account?.username ?? 'Signed in'} · {formatDate(new Date().toISOString())}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMigratingPool(true)}
              className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-muted"
            >
              <DatabaseZap className="h-4 w-4" />
              Migrate pool
            </button>
            <button
              onClick={() => setShowBackfills(true)}
              className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-muted"
            >
              <ClipboardCheck className="h-4 w-4" />
              Billing backfills
            </button>
            <button
              onClick={() => setShowEnablement(true)}
              className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-muted"
            >
              <Mail className="h-4 w-4" />
              Tenant email
            </button>
            <button
              onClick={() => setShowSuppressions(true)}
              className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-muted"
            >
              <MailX className="h-4 w-4" />
              Email suppressions
            </button>
            <button
              onClick={() => setMigratingAll(true)}
              className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-muted"
            >
              <DatabaseZap className="h-4 w-4" />
              Migrate all tenants
            </button>
            <button
              onClick={() => { adminLogout(); setSignedIn(false) }}
              className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
            >
              Sign out
            </button>
          </div>
        </div>
        <TenantList />
      </div>

      {migratingPool && <PoolMigrateDialog onClose={() => setMigratingPool(false)} />}
      {showBackfills && <BillingBackfillsDialog onClose={() => setShowBackfills(false)} />}
      {migratingAll && <MigrateAllDialog onClose={() => setMigratingAll(false)} />}
      {showSuppressions && <EmailSuppressionsDialog onClose={() => setShowSuppressions(false)} />}
      {showEnablement && <EmailEnablementDialog onClose={() => setShowEnablement(false)} />}
    </div>
  )
}
